import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { requestPasswordReset } from '../../auth/authApi'
import { Button, Field, Icon, I } from '../../components/ui'
import { AuthLayout } from './AuthLayout'

interface ForgotPasswordPageProps {
  onBackToLogin: () => void
}

export function ForgotPasswordPage({ onBackToLogin }: ForgotPasswordPageProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    try {
      await requestPasswordReset(email.trim())
    } catch {
      // Keep the same UI response to avoid account enumeration and match backend behavior.
    } finally {
      setSubmitted(true)
      setLoading(false)
    }
  }

  return (
    <AuthLayout
      eyebrow={t('auth.forgot.eyebrow')}
      title={submitted ? t('auth.forgot.titleSent') : t('auth.forgot.title')}
      subtitle={submitted ? t('auth.forgot.subtitleSent') : t('auth.forgot.subtitle')}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button type="button" onClick={onBackToLogin} style={{ border: 'none', background: 'transparent', color: 'var(--brand-700)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('auth.forgot.back')}
          </button>
        </div>
      )}
    >
      {submitted ? (
        <div style={{ display: 'grid', gap: 18 }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 18,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--success-100)',
            color: 'var(--success)',
          }}>
            <Icon size={24}>{I.check}</Icon>
          </div>
          <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 14, lineHeight: 1.6 }}>
            {t('auth.forgot.afterNote')}
          </p>
          <Button kind="secondary" size="lg" onClick={onBackToLogin} style={{ justifyContent: 'center', width: '100%' }}>
            {t('auth.forgot.returnToSignIn')}
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
          <Field label={t('auth.forgot.email')} type="email" placeholder={t('auth.forgot.emailPlaceholder')} value={email} onChange={event => setEmail(event.target.value)} />
          <Button type="submit" size="lg" disabled={loading} style={{ justifyContent: 'center', width: '100%' }}>
            {loading ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
